/**
 * Sync engine diagnostic panel (#1471 Phase 2).
 *
 * Hidden from regular navigation. Access via /debug/sync — protected by auth
 * like the rest of the app. Lets you manually enqueue test sync actions,
 * observe retry/backoff behavior, and inspect the visit_sync_queue store.
 *
 * Remove (or keep behind a stricter gate) once Phase 4 is stable.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSyncEngine, enqueueSyncAction, isSyncEngineEnabled } from '../hooks/useSyncEngine';

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString();
  } catch {
    return iso;
  }
}

export default function DebugSync() {
  const engine = useSyncEngine();
  const [flagEnabled, setFlagEnabled] = useState<boolean>(isSyncEngineEnabled());
  const [busy, setBusy] = useState<string | null>(null);

  // Refresh the enabled state when localStorage changes
  useEffect(() => {
    const onStorage = () => setFlagEnabled(isSyncEngineEnabled());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggleFlag = () => {
    const current = localStorage.getItem('visitSyncEnabled') === '1';
    if (current) localStorage.removeItem('visitSyncEnabled');
    else localStorage.setItem('visitSyncEnabled', '1');
    setFlagEnabled(!current);
  };

  const enqueue = async (label: string, action: Parameters<typeof enqueueSyncAction>[0]) => {
    setBusy(label);
    try {
      await enqueueSyncAction(action);
      await engine.refresh();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 pb-20">
      <div className="mx-auto max-w-3xl">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Sync Engine Diagnostics</h1>
          <Link to="/" className="text-sm text-primary-600 hover:underline">
            ← Home
          </Link>
        </div>

        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <p className="font-semibold">Dev-only page — #1471 Phase 2</p>
          <p>This page lets you test the sync engine in isolation before real visit hooks depend on it.</p>
        </div>

        {/* Engine state */}
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Pending" value={engine.pendingCount} />
          <StatCard label="Failed" value={engine.failedCount} />
          <StatCard label="Status" value={engine.isSyncing ? 'syncing' : 'idle'} />
          <StatCard label="Last sync" value={formatTime(engine.lastSyncAt)} />
        </div>

        {/* Feature flag */}
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold">Background tick enabled</div>
              <div className="text-sm text-slate-600">
                When on, sync runs every 2 min + on online/visibilitychange events.
                Manual "Sync Now" works either way.
              </div>
            </div>
            <button
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                flagEnabled
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-slate-200 text-slate-800 hover:bg-slate-300'
              }`}
              onClick={toggleFlag}
            >
              {flagEnabled ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>

        {/* Test actions */}
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-3 font-semibold">Enqueue test actions</h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <TestButton
              disabled={busy !== null}
              label="Success (GET /api/associates/associates/)"
              onClick={() =>
                enqueue('ok', {
                  action: 'test_success',
                  endpoint: '/api/associates/associates/',
                  method: 'GET',
                })
              }
            />
            <TestButton
              disabled={busy !== null}
              label="404 permanent fail (no retry)"
              onClick={() =>
                enqueue('404', {
                  action: 'test_404',
                  endpoint: '/api/nonexistent-endpoint-xyz/',
                  method: 'GET',
                })
              }
            />
            <TestButton
              disabled={busy !== null}
              label="400 permanent fail (bad body)"
              onClick={() =>
                enqueue('400', {
                  action: 'test_400',
                  endpoint: '/api/projects/calllog/',
                  method: 'POST',
                  payload: { intentionally: 'invalid', missing: 'required_fields' },
                })
              }
            />
            <TestButton
              disabled={busy !== null}
              label="Unreachable host (network fail, retries)"
              onClick={() =>
                enqueue('net', {
                  action: 'test_network_fail',
                  endpoint: '/api/__unreachable__/',
                  method: 'POST',
                  payload: { ping: Date.now() },
                  maxRetries: 3,
                })
              }
            />
          </div>
        </div>

        {/* Controls */}
        <div className="mb-4 flex flex-wrap gap-2">
          <button
            disabled={engine.isSyncing}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            onClick={() => engine.syncNow()}
          >
            {engine.isSyncing ? 'Syncing…' : 'Sync Now'}
          </button>
          <button
            className="rounded-lg bg-slate-200 px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-300"
            onClick={() => engine.refresh()}
          >
            Refresh
          </button>
          <button
            className="rounded-lg bg-red-100 px-4 py-2 text-sm font-medium text-red-800 hover:bg-red-200"
            onClick={() => {
              if (confirm('Delete ALL sync queue entries?')) engine.clearAll();
            }}
          >
            Clear Queue
          </button>
        </div>

        {engine.lastResult && (
          <div className="mb-4 rounded-lg bg-slate-100 p-3 text-sm">
            <span className="font-semibold">Last run:</span>{' '}
            attempted {engine.lastResult.attempted} ·
            succeeded {engine.lastResult.succeeded} ·
            failed {engine.lastResult.failed} ·
            skipped {engine.lastResult.skipped}
          </div>
        )}

        {/* Queue */}
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-3 font-semibold">
            Queue ({engine.entries.length} {engine.entries.length === 1 ? 'entry' : 'entries'})
          </h2>
          {engine.entries.length === 0 ? (
            <div className="text-sm text-slate-500">Queue is empty.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-2">#</th>
                    <th className="py-2 pr-2">Action</th>
                    <th className="py-2 pr-2">Endpoint</th>
                    <th className="py-2 pr-2">Status</th>
                    <th className="py-2 pr-2">Retries</th>
                    <th className="py-2 pr-2">Last attempt</th>
                    <th className="py-2 pr-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {engine.entries.map((e) => (
                    <tr key={e.id} className="border-t border-slate-100">
                      <td className="py-2 pr-2 font-mono text-xs">{e.id}</td>
                      <td className="py-2 pr-2">{e.action}</td>
                      <td className="py-2 pr-2 font-mono text-xs">
                        {e.method} {e.endpoint}
                      </td>
                      <td className="py-2 pr-2">
                        <StatusBadge status={e.status} />
                      </td>
                      <td className="py-2 pr-2">
                        {e.retries}/{e.maxRetries}
                      </td>
                      <td className="py-2 pr-2 text-xs text-slate-600">
                        {formatTime(e.lastAttempt)}
                      </td>
                      <td className="py-2 pr-2">
                        <button
                          className="text-xs text-red-600 hover:underline"
                          onClick={() => engine.deleteEntry(e.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {engine.entries.some((e) => e.error) && (
            <div className="mt-4 space-y-1 text-xs text-slate-600">
              <div className="font-semibold">Errors:</div>
              {engine.entries
                .filter((e) => e.error)
                .map((e) => (
                  <div key={`err-${e.id}`} className="font-mono">
                    #{e.id}: {e.error}
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function TestButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      disabled={disabled}
      className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm hover:bg-slate-100 disabled:opacity-50"
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === 'pending'
      ? 'bg-yellow-100 text-yellow-800'
      : status === 'syncing'
      ? 'bg-blue-100 text-blue-800'
      : status === 'failed'
      ? 'bg-red-100 text-red-800'
      : 'bg-green-100 text-green-800';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${color}`}>
      {status}
    </span>
  );
}
